"use client";

import { Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon, XIcon } from "lucide-react";
import Image from "next/image";
import { Fragment, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useChat } from "@/providers/chat-provider";

type StickyBottomAdProps = {
	productName: string;
	productLogo: string;
	productTagline: string;
	description: string;
	demoLink: string;
	popupImage: string;
	popupVideoLink: string;
};

export default function StickyBottomAd({
	productName,
	productLogo,
	productTagline,
	description,
	demoLink,
	popupImage,
	popupVideoLink,
}: StickyBottomAdProps) {
	const [isMinimized, setIsMinimized] = useState(true);
	const [showVideoModal, setShowVideoModal] = useState<boolean>(false);
	const [scrollY, setScrollY] = useState<number>(0);
	const { showChatWidget } = useChat();

	const show = scrollY > 300;

	useEffect(() => {
		const handleScroll = () => {
			if (scrollY > 1000) {
				setIsMinimized(false);
			} else {
				setIsMinimized(true);
			}
			setScrollY(window.scrollY);
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, [scrollY]);

	return (
		<AnimatePresence>
			{show ? (
				isMinimized ? (
					<motion.div
						key="minimizedBottombar"
						initial={{ y: "100%" }}
						animate={{ y: 0 }}
						exit={{ y: "100%" }}
						transition={{ duration: 0.5, ease: "easeOut" }}
						className={cn(
							"fixed bottom-0 left-0 z-20 flex flex-row flex-wrap items-center justify-between bg-[url('/images/listing-page/sticky-footer-bg.svg')] bg-cover bg-no-repeat md:flex-nowrap",
							showChatWidget ? "w-[calc(100%-16rem)]" : "w-full"
						)}
					>
						<div className="mx-auto flex w-11/12 max-w-6xl items-center justify-center gap-4 py-4 max-md:flex-wrap md:justify-between lg:w-full">
							<div className="flex w-full items-center gap-4">
								<div className="h-12 w-12">
									<Image
										src={productLogo}
										alt={productName}
										className="h-full w-full rounded border-2 border-yellow-600 bg-white object-contain"
										width={80}
										height={80}
									/>
								</div>

								<h5 className="font-bold text-white">{productName}</h5>
								<span className="text-sm text-white">{productTagline}</span>
							</div>

							<div className="flex items-center gap-4">
								<a
									href={demoLink}
									className="flex w-max items-center justify-center gap-3 rounded-lg bg-gradient-to-r from-[#005EFF] via-[#642CFD] to-[#9613FC] px-8 py-2 text-sm font-medium uppercase text-white shadow"
								>
									Get Free Demo
								</a>

								<button onClick={() => setIsMinimized(false)}>
									<ChevronUpIcon className="stroke-white" />
								</button>
							</div>
						</div>
					</motion.div>
				) : (
					<motion.div
						key="expandedBottombar"
						initial={{ y: "100%" }}
						animate={{ y: 0 }}
						exit={{ y: "100%" }}
						transition={{ duration: 0.3, ease: "easeOut" }}
						className={cn(
							"fixed bottom-0 left-0 z-20 bg-[#1a2133] bg-cover bg-no-repeat",
							showChatWidget ? "w-[calc(100%-18rem)]" : "w-full"
						)}
					>
						<div className="relative">
							<div className="mx-auto w-11/12 max-w-5xl pb-4 pt-3 lg:w-full">
								<div className="flex items-start justify-start gap-4 pb-3">
									<div className="-mt-10 h-24 w-24 shadow">
										<Image
											src={productLogo}
											alt={productName}
											className="h-full w-full rounded border-2 bg-white border-yellow-600 object-contain"
											width={480}
											height={480}
										/>
									</div>

									<div className="space-y-2">
										<h4 className="text-base font-bold text-white">{productName}</h4>
										<p className="text-sm font-semibold text-white">{productTagline}</p>
									</div>
								</div>

								<div className="mt-1 w-full rounded border border-gray-50/25"></div>

								<div className="flex items-center justify-center gap-2 pt-4 max-md:flex-wrap md:justify-start">
									<h2 className="text-sm font-medium text-gray-200 md:text-lg">{description} —</h2>

									<a
										href={demoLink}
										className="text-lg font-bold text-white"
										target="_blank"
										rel="noopener noreferrer"
									>
										<span>Request Demo</span>
										<div className="-mt-[10px] w-full bg-gradient-to-r from-[#005EFF] via-[#642CFD] to-[#9613FC] p-1"></div>
									</a>
								</div>

								<div className="absolute bottom-3/4 right-5 cursor-pointer md:bottom-8 md:right-32">
									<div className="relative">
										<button type="button" onClick={() => setShowVideoModal(true)}>
											<Image
												src={popupImage}
												alt={productName}
												className="h-48 w-full rounded border-2 border-yellow-600 object-contain md:h-80"
												width={1080}
												height={1080}
											/>
										</button>

										<button
											type="button"
											className="absolute -top-11 right-0 rounded bg-white p-2 shadow"
											onClick={() => setIsMinimized(true)}
										>
											<ChevronDownIcon className="h-6 w-6 text-gray-500" />
										</button>
									</div>
								</div>
							</div>
						</div>
					</motion.div>
				)
			) : null}

			{/* Video popup */}
			<Transition appear show={showVideoModal}>
				<>
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-200"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<div className="fixed inset-0 z-30 bg-black/75 backdrop-blur-sm" />
					</Transition.Child>

					<div className="fixed inset-0 z-50 overflow-y-auto">
						<div className="z-50 flex min-h-full items-center justify-center p-4 pt-20 text-center">
							<Transition.Child
								as={Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0 scale-95"
								enterTo="opacity-100 scale-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100 scale-100"
								leaveTo="opacity-0 scale-95"
							>
								<div className="relative w-full max-w-5xl transform transition-all">
									<button
										className="absolute bottom-full right-0 mb-2"
										onClick={() => setShowVideoModal(false)}
									>
										<XIcon className="h-9 w-9 stroke-1 text-gray-400 transition hover:text-gray-50" />
									</button>

									<iframe
										src={popupVideoLink}
										title={`${productName} promo video`}
										allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
										allowFullScreen
										className="aspect-video h-full w-full shadow-xl"
									/>
								</div>
							</Transition.Child>
						</div>
					</div>
				</>
			</Transition>
		</AnimatePresence>
	);
}
